"use client";

import React, { useState, useEffect, useRef } from "react";
import Button from "../../../components/Button";
import Footer from "../../../components/footer";
import Popup from "../../../components/Popup";
import overlayStyles from "../../../css/components/overlaymodal-animations.module.css";
import styles from "../css/patientSignature.module.css";
import CertDocPrescription from "../../Dispensary/Chronic/components/CertDocPrescription";
import ClinicInfo from "../../Dispensary/Chronic/components/ClinicInfo";
import PatientDeclaration from "../../Dispensary/Chronic/components/PatientDeclaration";
import { CompletedPopup } from "../../Dispensary/ToFollows/views/FollowUpSignatureFlow.jsx";
import JsBarcode from "jsbarcode";

export default function DoctorePrescription({
  isOpen,
  onClose,
  patientInfo = {
    name: "Morris. T. Mrs",
    patient: "Monica Morris",
    profile: "998565",
    address: ["189 Oak Lane", "Pinetown", "Durban", "3600"],
    phone: "0836470948",
    medicalAid: "Discovery",
  },
  prescription = {
    title: "CERTIFIED COPY OF DOCTORS PRESCRIPTION",
    lines: [
      {
        code: "830747001",
        drug: "ZOXIL SF 250MG SUSP",
        qty: "30",
        side: "OD",
        icd10: "Z76.9",
        price: "R 32.23",
        instructions: [
          "5MLS THREE TIMES A DAY AFTER MEALS FOR 5 DAYS",
          "*ANTIBIOTIC – FINISH THE COURSE *",
          "KEEP REFRIGERATED",
        ],
      },
      {
        code: "717602001",
        drug: "PHARGEL GEO",
        qty: "10",
        side: "OD",
        icd10: "Z76.9",
        price: "R 116.08",
        instructions: ["APPLY TO GUM AREA WHEN NECESSARY"],
      },
    ],
    pharmacy: "Durban North Pharmacy",
    registration: "0602809",
    copyCost: "",
    total: "",
  },
  pharmacistSignature = null,
  patientSignature = null,
  receiptNumber = null,
  prescriptionId = null,
  totals = { amount: "", vat: "", total: "" },
  useCompletedPopup = false,
  onComplete,
}) {
  const [popupMsg, setPopupMsg] = useState("");
  const [showCompleted, setShowCompleted] = useState(false);
  const [barcodeValue, setBarcodeValue] = useState(null);
  const barcodeRef = useRef(null);

  const completedDonePath = (() => {
    try {
      const p = typeof window !== "undefined" ? window.location.pathname || "" : "";
      if (p.includes("/customer/dispensary/otc-direct")) return "/customer/dispensary/otcCustomer";
      if (p.includes("/customer/dispensary/amend-script")) return "/customer/dispensary/ammend-script";
      if (p.includes("/customer/dispensary/price-script")) return "/customer/dispensary/script";
      if (p.includes("/customer/dispensary/script/payment-summary")) return "/customer/dispensary/script";
      return "";
    } catch {
      return "";
    }
  })();

  // Generate barcode when receiptNumber is available
  useEffect(() => {
    if (receiptNumber && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, receiptNumber, {
          format: "CODE128",
          displayValue: false,
          width: 2,
          height: 80,
          margin: 8,
          background: "transparent",
          lineColor: "#000000",
        });
        // Store the barcode value for passing through navigation
        setBarcodeValue(receiptNumber);
        // Also store in sessionStorage for persistence
        sessionStorage.setItem('lastBarcodeValue', receiptNumber);
        console.log('[DoctorePrescription] Generated and stored barcode value:', receiptNumber);
      } catch (error) {
        console.error("Error generating barcode:", error);
      }
    }
  }, [receiptNumber]);

  if (!isOpen) return null;

  const now = new Date();
  const currentDate = now.toLocaleDateString("en-ZA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const currentTime = now.toLocaleTimeString("en-ZA", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const doctorName = prescription?.doctorName || "";
  const practiceNumber = prescription?.practiceNumber || "";
  const scriptRef = prescription?.scriptRef || "";
  const itemsCount =
    typeof prescription?.itemsCount === "number"
      ? prescription.itemsCount
      : Array.isArray(prescription?.lines)
      ? prescription.lines.length
      : 0;

  const Barcode = ({ className }) => (
    <div className={`${styles.barcode} ${className || ""}`} aria-label="barcode">
      <span />
      <span />
      <span />
      <span />
      <span />
      <span />
      <span />
      <span />
      <span />
      <span />
      <span />
      <span />
    </div>
  );

  return (
    <>
      <div id="doctor-prescription-print-root">
        <div className={`${overlayStyles.modalOverlay} ${overlayStyles.show}`}>
          <div className={`${overlayStyles.modalContent} ${styles.modalContentLarge}`}>
            {/* Close button in top right */}
            <button 
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close prescription preview"
            >
              ×
            </button>
            <div className={styles.scrollArea}>
              <div className={styles.sheet}>
                {/* Patient Information */}
                <section className={styles.card}>
                  <h3 className={styles.cardTitle}>PATIENT INFORMATION</h3>
                  <div className={styles.infoLines}>
                    <div className={styles.infoLine}>
                      <span className={styles.label}>Name:</span>
                      <span className={styles.value}>{patientInfo.name}</span>
                    </div>
                    <div className={styles.infoLine}>
                      <span className={styles.label}>Patient:</span>
                      <span className={styles.value}>{patientInfo.patient}</span>
                    </div>
                    <div className={styles.infoLine}>
                      <span className={styles.label}>Profile:</span>
                      <span className={styles.value}>{patientInfo.profile}</span>
                    </div>
                    <div className={styles.infoLine}>
                      <span className={styles.label}>Address:</span>
                      <span className={styles.value}>
                        {Array.isArray(patientInfo.address)
                          ? patientInfo.address.map((l, i) => <div key={i}>{l}</div>)
                          : patientInfo.address}
                      </span>
                    </div>
                    <div className={styles.infoLine}>
                      <span className={styles.label}>Phone:</span>
                      <span className={styles.value}>{patientInfo.phone}</span>
                    </div>
                    <div className={styles.infoLine}>
                      <span className={styles.label}>Medical Aid:</span>
                      <span className={styles.value}>{patientInfo.medicalAid}</span>
                    </div>
                  </div>
                </section>

                {/* Certified Copy */}
                <CertDocPrescription
                  title={prescription.title}
                  lines={prescription.lines}
                  pharmacistSignature={pharmacistSignature}
                  pharmacyName={prescription.pharmacy}
                  pharmacyRegistration={prescription.registration}
                  copyCost={prescription.copyCost}
                  total={prescription.total}
                  receiptNumber={receiptNumber}
                />

                {/* Clinic/Prescription Info card */}
                <ClinicInfo
                  doctorName={doctorName}
                  practiceNumber={practiceNumber}
                  scriptRef={scriptRef}
                  dateStr={currentDate}
                  timeStr={currentTime}
                  itemsCount={itemsCount}
                />

                {/* Acknowledgement + patient signature (componentized) */}
                <PatientDeclaration totals={totals} patientSignature={patientSignature} />

                {/* Bottom bar with barcode and Print */}
                <div className={styles.bottomBar}>
                  <svg ref={barcodeRef} className={styles.bottomBarcode} role="img" aria-label={`Barcode for receipt ${receiptNumber || 'N/A'}`} />
                  <Button
                    variant="primary"
                    onClick={async () => {
                      try {
                        if (onComplete) {
                          await onComplete();
                        }
                        if (useCompletedPopup) {
                          setShowCompleted(true);
                        } else {
                          setPopupMsg("Collected");
                        }
                        if (
                          typeof window !== "undefined" &&
                          typeof document !== "undefined" &&
                          document.body
                        ) {
                          const container = document.getElementById(
                            "doctor-prescription-print-root"
                          );
                          if (container) {
                            const html = container.innerHTML;
                            if (html && html.trim().length > 0) {
                              const iframe = document.createElement("iframe");
                              iframe.style.position = "fixed";
                              iframe.style.right = "0";
                              iframe.style.bottom = "0";
                              iframe.style.width = "0";
                              iframe.style.height = "0";
                              iframe.style.border = "0";
                              document.body.appendChild(iframe);

                              const doc =
                                iframe.contentWindow?.document ||
                                iframe.contentDocument ||
                                null;
                              if (doc) {
                                let headHtml = "";
                                try {
                                  const headClone = document.head.cloneNode(true);
                                  const scripts = headClone.querySelectorAll("script");
                                  scripts.forEach((s) => s.parentNode && s.parentNode.removeChild(s));
                                  headHtml = headClone.innerHTML || "";
                                } catch (e) {
                                  headHtml = "";
                                }

                                doc.open();
                                doc.write(
                                  `<!doctype html><html><head>${headHtml}</head><body>${html}</body></html>`
                                );
                                doc.close();

                                iframe.onload = () => {
                                  try {
                                    iframe.contentWindow?.focus();
                                    iframe.contentWindow?.print();
                                  } finally {
                                    setTimeout(() => {
                                      document.body.removeChild(iframe);
                                    }, 500);
                                  }
                                };
                              } else {
                                document.body.removeChild(iframe);
                              }
                            }
                          }
                        }
                      } catch (e) {
                        setPopupMsg(e?.message || "Failed to complete prescription");
                      }
                    }}
                  >
                    Print
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {!useCompletedPopup && (
        <Popup type="success" message={popupMsg} onClose={() => setPopupMsg("")} />
      )}

      {useCompletedPopup && (
        <CompletedPopup 
          isOpen={showCompleted} 
          onClose={() => setShowCompleted(false)} 
          prescriptionId={prescriptionId}
          receiptNumber={receiptNumber}
          totals={totals}
          barcodeValue={barcodeValue}
          donePath={completedDonePath}
        />
      )}
      
      {/* Debug: Log what props are being passed */}
      {console.log('[DoctorePrescription] Props being passed to CompletedPopup:', {
        prescriptionId,
        receiptNumber,
        totals
      })}
    </>
  );
}

